import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type TradeInModuleSpecsFragment } from '../../../../api/fragments/TradeInModuleSpecs';
import {
    UpdateTradeInModuleDocument,
    type UpdateTradeInModuleMutation,
    type UpdateTradeInModuleMutationVariables,
} from '../../../../api/mutations/updateTradeInModule';
import { type TradeInModuleSettings } from '../../../../api/types';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import TradeInModuleUpdateForm from './TradeInModuleUpdateForm';

type TradeInModuleMainDetailsProps = {
    module: TradeInModuleSpecsFragment;
};

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('setting.baseUrl'),
    validators.requiredString('setting.clientId'),
    validators.requiredString('setting.clientSecret')
);

const TradeInModuleMainDetails = ({ module }: TradeInModuleMainDetailsProps) => {
    const { t } = useTranslation(['tradeInModuleDetails']);

    const apolloClient = useApolloClient();

    const { setting } = module;

    const initialValues = useMemo<TradeInModuleSettings>(
        () => ({
            displayName: module.displayName,
            setting: {
                baseUrl: setting?.baseUrl,
                clientId: setting?.clientId,
                clientSecret: setting?.clientSecret,
            },
        }),
        [module.displayName, setting]
    );

    const onSubmit = useHandleError(
        async (values: TradeInModuleSettings) => {
            if (!values.displayName) {
                return;
            }

            message.loading({
                content: t('tradeInModuleDetails:messages.updateMainDetailsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateTradeInModuleMutation, UpdateTradeInModuleMutationVariables>({
                    mutation: UpdateTradeInModuleDocument,
                    variables: {
                        moduleId: module.id,
                        settings: values,
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('tradeInModuleDetails:messages.updateMainDetailsSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, t]
    );

    const validate = useValidator(validator);

    return (
        <Formik<TradeInModuleSettings> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateTradeInModuleForm"
                    layout="vertical"
                    name="updateTradeInModuleForm"
                    onSubmitCapture={handleSubmit}
                >
                    <TradeInModuleUpdateForm module={module} />
                </Form>
            )}
        </Formik>
    );
};

export default TradeInModuleMainDetails;
